import { minutesToMilliseconds } from 'date-fns';
import {
  queryOptions,
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import {
  fetchAllUsers,
  fetchUser,
  login,
  updateUser,
} from 'services/backend/users';
import { ReactQueryFunctionCallbacks } from 'types/internal';

export const userQueries = {
  get: (userId: number | undefined) =>
    queryOptions({
      queryFn: userId ? () => fetchUser({ userId }) : skipToken,
      queryKey: ['users', userId],
      staleTime: minutesToMilliseconds(10),
    }),
  list: ({ role }: { role?: string } = {}) =>
    queryOptions({
      queryFn: () => fetchAllUsers({ role }),
      queryKey: ['users', { role }],
      staleTime: minutesToMilliseconds(10),
    }),
};

export function useLogin(opts: ReactQueryFunctionCallbacks<typeof login>) {
  return useMutation({
    ...opts,
    meta: { sentryIgnoreErrorCodes: [401] },
    mutationFn: login,
  });
}

export function useAllUsers({
  enabled = true,
  role,
}: {
  enabled?: boolean;
  role?: string;
} = {}) {
  const { data = [], isLoading } = useQuery({
    ...userQueries.list({ role }),
    enabled,
  });

  return {
    isLoadingUsers: isLoading,
    users: data,
  };
}

export function useUpdateUser(
  opts?: ReactQueryFunctionCallbacks<typeof updateUser>,
) {
  const queryClient = useQueryClient();

  return useMutation({
    ...opts,
    mutationFn: updateUser,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({
        queryKey: userQueries.get(args[0].id).queryKey,
      });

      opts?.onSuccess?.(...args);
    },
  });
}
